import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FaBars, FaTimes, FaPhone, FaEnvelope } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import API_URL from '../config/api';
import LanguageSwitcher from './LanguageSwitcher';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [companyInfo, setCompanyInfo] = useState(null);
  const location = useLocation();
  const { t } = useTranslation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    fetchCompanyInfo();
  }, []);

  const fetchCompanyInfo = async () => {
    try {
      const response = await axios.get(`${API_URL}/company`);
      if (response.data.success) {
        setCompanyInfo(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching company info:', error);
      // Set default values if API fails
      setCompanyInfo({
        phone: '(+62) 21-31118055',
        email: 'info@aftrustca.co.id'
      });
    }
  };

  const navLinks = [
    { name: t('common.home'), path: '/' },
    { name: t('common.about'), path: '/about' },
    { name: t('common.services'), path: '/services' },
    { name: t('common.management'), path: '/management' },
    { name: t('common.contact'), path: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      {/* Top Bar */}
      <div className="bg-gradient-to-r from-maroon-800 to-maroon-900 text-white py-3 hidden md:block border-b border-maroon-700">
        <div className="container mx-auto px-6 lg:px-16 xl:px-24">
          <div className="flex justify-between items-center text-sm">
            <div className="flex items-center space-x-6">
              {companyInfo?.phone && (
                <a 
                  href={`tel:${companyInfo.phone}`} 
                  className="flex items-center hover:text-gold-400 transition-colors group"
                >
                  <FaPhone className="mr-2 text-gold-400 group-hover:animate-bounce" size={14} />
                  <span className="font-medium">{companyInfo.phone}</span>
                </a>
              )}
              {companyInfo?.email && (
                <a 
                  href={`mailto:${companyInfo.email}`} 
                  className="flex items-center hover:text-gold-400 transition-colors group"
                >
                  <FaEnvelope className="mr-2 text-gold-400" size={14} />
                  <span className="font-medium">{companyInfo.email}</span>
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Navbar */}
      <nav className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled 
          ? 'bg-white shadow-elegant-lg backdrop-blur-md' 
          : 'bg-white shadow-elegant'
      }`}>
        <div className="container mx-auto px-6 lg:px-16 xl:px-24">
          <div className="flex justify-between items-center py-4">
            {/* Logo */}
            <Link to="/" className="flex items-center group">
              <div className="relative">
                <img 
                  src="/assets/transparan.jpg" 
                  alt="AFTRUSTCA" 
                  className="h-14 w-auto transition-transform duration-300 group-hover:scale-105"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
              <div className="ml-4">
                <h1 className="text-2xl font-display font-bold text-maroon-800 group-hover:text-maroon-700 transition-colors">
                  AFTRUSTCA
                </h1>
                <p className="text-xs text-gray-600 font-medium tracking-wide">
                  Sustainable • Global • Trust
                </p>
              </div>
            </Link>

            {/* Desktop Menu */}
            <div className="hidden lg:flex items-center space-x-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`relative px-4 py-2 font-semibold text-sm transition-all duration-300 rounded-lg ${
                    isActive(link.path)
                      ? 'text-white bg-maroon-700'
                      : 'text-gray-700 hover:text-maroon-700 hover:bg-gray-50'
                  }`}
                >
                  {link.name}
                  {isActive(link.path) && (
                    <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-gold-500"></span>
                  )}
                </Link>
              ))}
              
              {/* Language Switcher - Desktop */}
              <div className="ml-6 pl-6 border-l border-gray-300">
                <LanguageSwitcher />
              </div>
            </div>

            {/* Language Switcher - Mobile (only language, menu di bottom nav) */}
            <div className="lg:hidden">
              <LanguageSwitcher />
            </div>
          </div>
        </div>
      </nav>
    </>
  );
};

export default Navbar;
